import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Camera, FolderKanban, Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { claimTitle } from "@/lib/utils";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const NSW = [-32.3, 147.5];
const num = (v) => (v == null || v === "" || isNaN(v) ? null : Number(v));

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 12);
    else if (points.length > 1) map.fitBounds(points, { padding: [50, 50] });
  }, [points, map]);
  return null;
}

export default function RecoveryMap() {
  const [claims, setClaims] = useState([]);
  const [assets, setAssets] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState({ events: true, assets: true, photos: true });
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/fieldclaim/claims"), api.get("/fieldclaim/sync")])
      .then(([c, s]) => {
        setClaims(c.data.filter((x) => num(x.latitude) != null && num(x.longitude) != null));
        setAssets((s.data.assets || []).filter((a) => num(a.latitude) != null && num(a.longitude) != null));
        setPhotos((s.data.photos || []).filter((p) => num(p.latitude) != null && num(p.longitude) != null));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const claimName = useMemo(() => Object.fromEntries(claims.map((c) => [c.id, c.name])), [claims]);
  const points = useMemo(() => [
    ...(show.events ? claims : []).map((c) => [num(c.latitude), num(c.longitude)]),
    ...(show.assets ? assets : []).map((a) => [num(a.latitude), num(a.longitude)]),
    ...(show.photos ? photos : []).map((p) => [num(p.latitude), num(p.longitude)]),
  ].filter((p) => p[0] != null), [claims, assets, photos, show]);

  const toggle = (k) => setShow((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="space-y-6 text-white" data-testid="recovery-map-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Operational picture · recovery.map</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Recovery Map</h1>
          <p className="text-white/55 mt-3 font-light max-w-2xl">Every disaster event, geo-located damage asset and field photo across your portfolio — one live operational view.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="map-legend">
          <Chip active={show.events} onClick={() => toggle("events")} color="bg-gold-400" icon={Star} label={`Events · ${claims.length}`} testid="toggle-events" />
          <Chip active={show.assets} onClick={() => toggle("assets")} color="bg-sky-400" icon={FolderKanban} label={`Assets · ${assets.length}`} testid="toggle-assets" />
          <Chip active={show.photos} onClick={() => toggle("photos")} color="bg-emerald-400" icon={Camera} label={`Photos · ${photos.length}`} testid="toggle-photos" />
        </div>
      </div>

      {loading ? (
        <div className="h-[420px] border border-gold-500/25 flex items-center justify-center text-white/50"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading map…</div>
      ) : points.length === 0 ? (
        <div className="h-[420px] border border-dashed border-gold-500/25 flex flex-col items-center justify-center text-white/45 font-light gap-2" data-testid="map-empty">
          <MapPin className="h-6 w-6 text-gold-500/50" />
          No geo-located data yet. Set an event point or capture GPS-tagged photos in FieldClaim.
        </div>
      ) : (
        <div className="border border-gold-500/25 overflow-hidden" style={{ height: "620px" }} data-testid="map-container">
          <MapContainer center={NSW} zoom={6} style={{ height: "100%", width: "100%", background: "#0b1220" }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            {show.photos && photos.map((p) => (
              <CircleMarker key={`p-${p.id}`} center={[num(p.latitude), num(p.longitude)]} radius={4} pathOptions={{ color: "#34d399", fillColor: "#34d399", fillOpacity: 0.7, weight: 1 }}>
                <Popup><b>Photo</b><br />{p.phase}<br />{claimName[p.claim_id] || ""}</Popup>
              </CircleMarker>
            ))}
            {show.assets && assets.map((a) => (
              <CircleMarker key={`a-${a.id}`} center={[num(a.latitude), num(a.longitude)]} radius={7} pathOptions={{ color: "#38bdf8", fillColor: "#38bdf8", fillOpacity: 0.8, weight: 2 }}>
                <Popup><b>{a.name}</b><br />{a.asset_type}<br />{claimName[a.claim_id] || ""}</Popup>
              </CircleMarker>
            ))}
            {show.events && claims.map((c) => (
              <CircleMarker key={`e-${c.id}`} center={[num(c.latitude), num(c.longitude)]} radius={11}
                pathOptions={{ color: "#d4a72c", fillColor: "#e9c46a", fillOpacity: 0.9, weight: 3 }}
                eventHandlers={{ click: () => {} }}>
                <Popup>
                  <b>{claimTitle(c)}</b><br />
                  {c.asset_count || 0} assets · {c.photo_count || 0} photos<br />
                  <button onClick={() => nav(`/dashboard/fieldclaim/${c.id}`)} style={{ marginTop: 6, background: "#d4a72c", color: "#1a1608", border: "none", padding: "4px 10px", cursor: "pointer", fontWeight: 600 }}>Open claim</button>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, color, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-3 py-2 border text-xs transition-colors ${active ? "border-gold-500/50 bg-navy-900 text-white" : "border-gold-500/15 text-white/40"}`}>
      <span className={`h-2.5 w-2.5 rounded-full ${color} ${active ? "" : "opacity-30"}`} />
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}
